<template>
  <div class="lms-agent-chat" :class="{ 'sidebar-open': sidebarOpen }">

    <!-- Sessions Sidebar -->
    <aside class="sessions-sidebar">
      <div class="sidebar-header">
        <h3><i class="fas fa-history"></i> Past Consults</h3>
        <button class="btn-new-session" @click="startNewSession" title="New consult">
          <i class="fas fa-plus"></i>
          <span>New</span>
        </button>
      </div>

      <div class="depth-toggle-row">
        <button
          :class="['depth-btn', { active: defaultDepth === 'quick' }]"
          @click="defaultDepth = 'quick'"
          title="Quick: fast answer with key sources"
        >
          <i class="fas fa-bolt"></i> Quick
        </button>
        <button
          :class="['depth-btn', { active: defaultDepth === 'deep' }]"
          @click="defaultDepth = 'deep'"
          title="Deep: comprehensive literature review"
        >
          <i class="fas fa-microscope"></i> Deep
        </button>
      </div>

      <div class="session-list">
        <div v-if="loadingSessions" class="sessions-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>

        <!-- Grouped sessions: Today / Yesterday / This Week / etc. -->
        <template v-if="!loadingSessions && groupedSessions.length > 0">
          <div v-for="group in groupedSessions" :key="group.label" class="session-group">
            <div class="session-group-label">{{ group.label }}</div>
            <div
              v-for="session in group.sessions"
              :key="session.id"
              :class="['session-item', { active: currentSessionId === session.id }]"
              @click="loadSessionById(session.id)"
            >
              <div class="session-icon">
                <i :class="consultTypeIcon(session.consult_type)"></i>
              </div>
              <div class="session-info">
                <span class="session-title">{{ session.title || 'Untitled Consult' }}</span>
                <span class="session-meta">
                  {{ session.detected_species || session.consult_type }}
                </span>
              </div>
              <i
                v-if="session.notify_on_new_evidence"
                class="fas fa-bell session-notify-icon"
                title="Evidence alerts on"
              ></i>
              <button
                class="session-delete-btn"
                title="Delete consult"
                @click.stop="deleteSession(session.id)"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </template>

        <div v-if="sessions.length === 0 && !loadingSessions" class="sessions-empty">
          <i class="fas fa-comment-medical"></i>
          <span>No previous consults</span>
        </div>
      </div>
    </aside>

    <!-- Main Chat Area -->
    <div class="chat-main">

      <!-- Top Bar -->
      <div class="chat-topbar">
        <button
          class="sidebar-toggle-btn"
          @click="sidebarOpen = !sidebarOpen"
          :title="sidebarOpen ? 'Hide history' : 'Show session history'"
        >
          <i class="fas fa-history"></i>
          <span class="toggle-label">{{ sidebarOpen ? 'Hide' : 'History' }}</span>
        </button>

        <div class="topbar-center">
          <router-link
            v-if="patientContext"
            :to="patientChartRoute"
            class="patient-context-chip"
            title="Open patient chart"
          >
            <i class="fas fa-paw"></i>
            Consulting for: {{ patientContext.name }}
          </router-link>
          <span v-if="currentSession && currentSession.detected_species" class="species-tag">
            <i class="fas fa-paw"></i> {{ currentSession.detected_species }}
          </span>
          <span
            v-if="currentEvidenceQuality"
            class="evidence-badge"
            :class="'quality-' + currentEvidenceQuality"
          >
            {{ evidenceQualityLabels[currentEvidenceQuality] || currentEvidenceQuality }}
          </span>
        </div>

        <div class="topbar-right">
          <!-- Usage meter chip -->
          <div v-if="usageQuota" class="usage-meter-chip" :class="{ 'usage-warn': usagePercent >= 70, 'usage-danger': usagePercent >= 90 }" title="AI token usage this billing period">
            <div class="usage-chip-bar">
              <div class="usage-chip-fill" :class="usageBarClass" :style="{ width: usagePercent + '%' }"></div>
            </div>
            <span class="usage-chip-label">{{ usagePercent }}%</span>
          </div>

          <button
            v-if="currentSessionId"
            :class="['notify-btn', { active: currentSession && currentSession.notify_on_new_evidence }]"
            @click="toggleNotifications"
            :title="currentSession && currentSession.notify_on_new_evidence ? 'Evidence alerts on — click to disable' : 'Get email alerts for new studies'"
          >
            <i :class="currentSession && currentSession.notify_on_new_evidence ? 'fas fa-bell' : 'far fa-bell'"></i>
            <span class="notify-label">{{ currentSession && currentSession.notify_on_new_evidence ? 'Alerts on' : 'Alert me' }}</span>
          </button>
        </div>
      </div>

      <!-- Messages / Welcome -->
      <div class="messages-container" ref="messagesContainer">

        <!-- Welcome State -->
        <div v-if="messages.length === 0 && !loading" class="welcome-state">
          <div class="welcome-icon">
            <i class="fas fa-robot"></i>
          </div>
          <h2>Clinical AI + Course Discovery</h2>
          <p class="welcome-subtitle">
            Get evidence-based answers backed by peer-reviewed research, with relevant CE courses surfaced alongside every response.
          </p>
          <div class="suggested-prompts">
            <h3>Try asking…</h3>
            <div class="prompt-grid">
              <button
                v-for="prompt in suggestedPrompts"
                :key="prompt"
                class="prompt-chip"
                @click="usePrompt(prompt)"
              >
                <i class="fas fa-comment-medical"></i>
                {{ prompt }}
              </button>
            </div>
          </div>
        </div>

        <!-- Message Thread -->
        <template v-else>
          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            :id="'message-' + idx"
            :class="['message', `message-${msg.role}`]"
          >
            <!-- Document upload turn -->
            <template v-if="msg.role === 'document'">
              <div class="message-doc-turn">
                <i class="fas fa-paperclip"></i>
                <span class="doc-filename">{{ msg.filename }}</span>
                <span class="doc-size">{{ formatFileSize(msg.file_size) }}</span>
                <span class="doc-status">Added to context</span>
              </div>
            </template>

            <!-- User turn -->
            <template v-else-if="msg.role === 'user'">
              <div class="message-avatar user-avatar">
                <i class="fas fa-user-md"></i>
              </div>
              <div class="message-body">
                <div class="message-content user-content">{{ msg.content }}</div>
              </div>
            </template>

            <!-- Assistant turn -->
            <template v-else>
              <div class="message-avatar ai-avatar">
                <i class="fas fa-robot"></i>
              </div>
              <div class="message-body">

                <!-- AI Answer -->
                <div class="message-content ai-content" v-html="renderMarkdown(msg.content, msg.sources)"></div>

                <div class="message-toolbar">
                  <button
                    class="download-response-btn"
                    title="Download response"
                    @click="downloadResponse(msg.content, idx)"
                  >
                    <i class="fas fa-download"></i> Download
                  </button>
                  <button
                    class="feedback-btn"
                    :class="{ active: msg.feedback?.kind === 'up' }"
                    :disabled="msg.feedback?.kind === 'up'"
                    title="This answer was helpful"
                    @click="recordFeedback(idx, 'up')"
                  >
                    <i class="fas fa-thumbs-up"></i>
                  </button>
                  <button
                    class="feedback-btn"
                    :class="{ active: msg.feedback?.kind === 'down' }"
                    title="This answer was not helpful"
                    @click="recordFeedback(idx, 'down')"
                  >
                    <i class="fas fa-thumbs-down"></i>
                  </button>
                </div>
                <div v-if="feedbackFor === idx && feedbackKind === 'down'" class="correction-box">
                  <textarea
                    v-model="correctionText"
                    rows="2"
                    placeholder="Optional: what was wrong or missing?"
                  ></textarea>
                  <div class="correction-actions">
                    <button type="button" class="correction-cancel" @click="cancelFeedback">Cancel</button>
                    <button type="button" class="correction-submit" @click="submitCorrection">Send feedback</button>
                  </div>
                </div>

                <!-- Corpus lanes: Evidence → Clinical Practice Cases → Clips → Courses -->
                <div
                  v-if="msg.role === 'assistant' && hasCorpusLanes(msg)"
                  class="corpus-lanes"
                >
                  <div class="corpus-lane-tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      class="corpus-lane-tab"
                      :class="{ active: activeCorpusLane(idx) === 'evidence' }"
                      :aria-selected="activeCorpusLane(idx) === 'evidence'"
                      @click="setCorpusLane(idx, 'evidence')"
                    >
                      <i class="fas fa-flask"></i>
                      Evidence
                      <span v-if="msg.sources?.length" class="count-badge">{{ msg.sources.length }}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      class="corpus-lane-tab corpus-lane-tab--cases"
                      :class="{ active: activeCorpusLane(idx) === 'cases' }"
                      :aria-selected="activeCorpusLane(idx) === 'cases'"
                      @click="setCorpusLane(idx, 'cases')"
                    >
                      <i class="fas fa-notes-medical"></i>
                      Clinical Practice Cases
                      <span v-if="msg.similarCases?.length" class="count-badge">{{ msg.similarCases.length }}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      class="corpus-lane-tab"
                      :class="{ active: activeCorpusLane(idx) === 'clips' }"
                      :aria-selected="activeCorpusLane(idx) === 'clips'"
                      @click="setCorpusLane(idx, 'clips')"
                    >
                      <i class="fas fa-film"></i>
                      Related Clips
                      <span v-if="msg.videoMoments?.length" class="count-badge">{{ msg.videoMoments.length }}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      class="corpus-lane-tab"
                      :class="{ active: activeCorpusLane(idx) === 'courses' }"
                      :aria-selected="activeCorpusLane(idx) === 'courses'"
                      @click="setCorpusLane(idx, 'courses')"
                    >
                      <i class="fas fa-graduation-cap"></i>
                      Related CE Courses
                      <span v-if="msg.courses?.length" class="count-badge">{{ msg.courses.length }}</span>
                    </button>
                  </div>

                  <!-- Evidence lane -->
                  <div v-show="activeCorpusLane(idx) === 'evidence'" class="corpus-lane-panel message-sources">
                    <div v-if="msg.sources && msg.sources.length > 0" class="sources-list">
                    <div
                      v-for="source in (expandedAllSources.has(idx) ? msg.sources : msg.sources.slice(0, 3))"
                      :key="source.pmid"
                      class="source-item"
                    >
                      <div class="source-top-row">
                        <div class="source-meta-row">
                          <span
                            class="source-db-badge"
                            :class="`db-badge-${(source.source_db || 'PubMed').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}`"
                          >{{ source.source_db || 'PubMed' }}</span>
                          <span class="source-journal">{{ source.journal }}</span>
                          <span v-if="source.year" class="source-year">{{ source.year }}</span>
                        </div>
                        <button
                          v-if="isSaveableSource(source)"
                          class="save-article-btn"
                          :class="{ saved: savedPmids.has(source.pmid) }"
                          :disabled="savingPmids.has(source.pmid)"
                          :title="savedPmids.has(source.pmid) ? 'Saved to library' : 'Save to library'"
                          @click="saveArticle(source, msg)"
                        >
                          <i v-if="savingPmids.has(source.pmid)" class="fas fa-spinner fa-spin"></i>
                          <i v-else-if="savedPmids.has(source.pmid)" class="fas fa-bookmark"></i>
                          <i v-else class="far fa-bookmark"></i>
                        </button>
                      </div>
                      <a
                        :href="source.url || `https://pubmed.ncbi.nlm.nih.gov/${source.pmid}/`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="source-title-link"
                      >
                        <p class="source-title">{{ source.title }}</p>
                      </a>
                      <p v-if="source.rationale" class="source-rationale">
                        <i class="fas fa-lightbulb"></i> {{ source.rationale }}
                      </p>
                      <div class="source-footer-row">
                        <span class="source-pmid">{{ sourceIdLabel(source) }}</span>
                        <a
                          v-if="source.open_access_pdf"
                          :href="source.open_access_pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="oa-pdf-link"
                        >
                          <i class="fas fa-file-pdf"></i> PDF
                        </a>
                      </div>
                    </div>
                    <button
                      v-if="msg.sources.length > 3"
                      class="sources-see-more-btn"
                      @click="toggleShowAllSources(idx)"
                    >
                      <i :class="expandedAllSources.has(idx) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                      {{ expandedAllSources.has(idx) ? 'Show less' : `See ${msg.sources.length - 3} more source${msg.sources.length - 3 !== 1 ? 's' : ''}` }}
                    </button>
                    </div>
                    <div v-else class="corpus-lane-empty">
                      <i class="fas fa-search"></i>
                      No research sources matched this query.
                    </div>
                  </div>

                  <!-- Clips lane -->
                  <div v-show="activeCorpusLane(idx) === 'clips'" class="corpus-lane-panel inline-moments">
                    <div v-if="msg.videoMoments && msg.videoMoments.length > 0" class="moments-list">
                      <button
                        v-for="moment in msg.videoMoments"
                        :key="moment.moment_id"
                        class="moment-chip"
                        @click="jumpToMoment(moment)"
                      >
                        <span class="moment-time">
                          <i :class="moment.is_enrolled ? 'fas fa-play-circle' : 'fas fa-eye'"></i>
                          {{ moment.formatted_start }}
                        </span>
                        <span class="moment-body">
                          <span class="moment-title">{{ moment.title }}</span>
                          <span class="moment-meta">
                            {{ moment.video_title }}
                            <template v-if="!moment.is_enrolled"> · Free preview</template>
                          </span>
                          <span v-if="moment.description" class="moment-why">
                            {{ truncateText(moment.description, 160) }}
                          </span>
                          <span v-if="moment.match_reason" class="moment-match-reason">
                            <i class="fas fa-check-circle"></i>
                            {{ truncateText(moment.match_reason, 120) }}
                          </span>
                          <blockquote
                            v-if="moment.relevance_citation && moment.relevance_citation.quote"
                            class="moment-citation-inline"
                          >
                            <span v-if="moment.relevance_citation.timestamp" class="citation-time">
                              {{ moment.relevance_citation.timestamp }}
                            </span>
                            "{{ truncateText(moment.relevance_citation.quote, 140) }}"
                          </blockquote>
                          <span v-if="moment.keywords && moment.keywords.length" class="moment-keywords">
                            <span
                              v-for="kw in moment.keywords.slice(0, 3)"
                              :key="kw"
                              class="keyword-tag"
                            >{{ kw }}</span>
                          </span>
                        </span>
                      </button>
                    </div>
                    <div v-else class="corpus-lane-empty">
                      <i class="fas fa-film"></i>
                      No matching video clips for this query.
                    </div>
                  </div>

                  <!-- Courses lane -->
                  <div v-show="activeCorpusLane(idx) === 'courses'" class="corpus-lane-panel inline-courses">
                    <div v-if="msg.courses && msg.courses.length > 0" class="courses-grid">
                      <div class="inline-courses-header">
                        <button class="view-catalog-link" @click="$emit('switch-tab', 'browse')">
                          Browse all <i class="fas fa-arrow-right"></i>
                        </button>
                      </div>
                      <CourseCard
                        v-for="course in msg.courses"
                        :key="course.id"
                        compact
                        :course="normalizeForCourseCard(course)"
                        @view-details="handleViewCourse"
                        @enroll="handleEnroll"
                      />
                    </div>
                    <div v-else class="corpus-lane-empty">
                      <i class="fas fa-graduation-cap"></i>
                      No matching courses for this query.
                      <button class="view-catalog-link-inline" @click="$emit('switch-tab', 'browse')">
                        Browse the catalog
                      </button>
                    </div>
                  </div>

                  <!-- Clinical Practice Cases lane -->
                  <div v-show="activeCorpusLane(idx) === 'cases'" class="corpus-lane-panel cases-lane-panel">
                    <div v-if="msg.similarCases && msg.similarCases.length > 0" class="cases-list">
                      <template v-for="(caze, ci) in msg.similarCases" :key="caze.id || ci">
                        <!-- Practice pattern overview / diagnosis drill-down -->
                        <div
                          v-if="caze.kind === 'practice_pattern' && caze.pattern"
                          class="case-card case-card--pattern"
                        >
                          <span class="case-kind">Clinical practice pattern</span>
                          <span class="case-title">{{ caze.presenting_complaint || caze.diagnosis || 'Practice pattern' }}</span>
                          <span v-if="caze.species?.length" class="case-meta">
                            {{ caze.species.join(', ') }}
                          </span>

                          <!-- Drill-down for selected diagnosis -->
                          <template v-if="selectedDiagnosisSlice(idx, caze)">
                            <button
                              type="button"
                              class="case-slice-back"
                              @click="clearDiagnosisSlice(idx)"
                            >
                              <i class="fas fa-arrow-left"></i>
                              Back to practice pattern
                            </button>
                            <div class="case-slice-header">
                              <h4>{{ selectedDiagnosisSlice(idx, caze).label }}</h4>
                              <span class="case-pattern-count">
                                {{ selectedDiagnosisSlice(idx, caze).count }}
                                ({{ selectedDiagnosisSlice(idx, caze).pct }}%)
                              </span>
                            </div>
                            <div
                              v-for="section in diagnosisSliceSections(selectedDiagnosisSlice(idx, caze))"
                              :key="section.key"
                              class="case-pattern-facet"
                            >
                              <div class="case-pattern-facet-title">{{ section.title }}</div>
                              <ul class="case-pattern-dx">
                                <li v-for="(item, ii) in section.items" :key="ii">
                                  {{ item.label }}
                                  <span class="case-pattern-count">{{ item.count }} ({{ item.pct }}%)</span>
                                </li>
                              </ul>
                            </div>
                            <div
                              v-if="selectedDiagnosisSlice(idx, caze).exemplars?.length"
                              class="case-slice-exemplars"
                            >
                              <div class="case-pattern-facet-title">View Comparable Cases</div>
                              <div
                                v-for="(ex, ei) in selectedDiagnosisSlice(idx, caze).exemplars"
                                :key="ex.id || ei"
                                class="case-card case-card--exemplar case-card--nested"
                              >
                                <span class="case-kind">Comparable case</span>
                                <span class="case-title">{{ ex.presenting_complaint || 'Example case' }}</span>
                                <span v-if="ex.diagnosis" class="case-dx">{{ truncateText(ex.diagnosis, 200) }}</span>
                                <span v-if="ex.outcome_summary" class="case-outcome">{{ truncateText(ex.outcome_summary, 160) }}</span>
                              </div>
                            </div>
                          </template>

                          <!-- Overview: clickable Common Diagnoses -->
                          <template v-else>
                            <div
                              v-if="(caze.pattern.diagnoses || []).length"
                              class="case-pattern-facet"
                            >
                              <div class="case-pattern-facet-title">Common Diagnoses</div>
                              <ul class="case-pattern-dx case-pattern-dx--clickable">
                                <li
                                  v-for="(dx, di) in (caze.pattern.diagnoses || []).slice(0, 8)"
                                  :key="di"
                                >
                                  <button
                                    type="button"
                                    class="case-dx-link"
                                    :disabled="!diagnosisSliceFor(caze.pattern, dx.label)"
                                    :title="diagnosisSliceFor(caze.pattern, dx.label) ? 'View clinical patterns for this diagnosis' : 'No drill-down for this diagnosis yet'"
                                    @click="selectDiagnosisSlice(idx, dx.label)"
                                  >
                                    <span>{{ dx.label }}</span>
                                    <span class="case-pattern-count">{{ dx.count }} ({{ dx.pct }}%)</span>
                                    <i
                                      v-if="diagnosisSliceFor(caze.pattern, dx.label)"
                                      class="fas fa-chevron-right case-dx-chevron"
                                    ></i>
                                  </button>
                                </li>
                              </ul>
                            </div>
                            <div
                              v-for="section in patternOverviewSections(caze.pattern)"
                              :key="section.key"
                              class="case-pattern-facet case-pattern-facet--secondary"
                            >
                              <div class="case-pattern-facet-title">{{ section.title }}</div>
                              <ul class="case-pattern-dx">
                                <li v-for="(item, ii) in section.items" :key="ii">
                                  {{ item.label }}
                                  <span class="case-pattern-count">{{ item.count }} ({{ item.pct }}%)</span>
                                </li>
                              </ul>
                            </div>
                          </template>

                          <span v-if="caze.outcome_summary" class="case-outcome">
                            {{ truncateText(caze.outcome_summary, 320) }}
                          </span>
                        </div>

                        <!-- Pack-level exemplars / ANN similar cases (hide while drilled in) -->
                        <div
                          v-else-if="!selectedSliceByMsg[idx]"
                          class="case-card"
                          :class="{
                            'case-card--exemplar': caze.kind === 'exemplar_case',
                          }"
                        >
                          <span v-if="caze.kind === 'exemplar_case'" class="case-kind">
                            Example case
                          </span>
                          <span class="case-title">{{ caze.presenting_complaint || caze.diagnosis || 'Similar case' }}</span>
                          <span v-if="caze.species?.length" class="case-meta">
                            {{ caze.species.join(', ') }}
                          </span>
                          <span v-if="caze.diagnosis && caze.kind === 'exemplar_case'" class="case-dx">
                            {{ truncateText(caze.diagnosis, 200) }}
                          </span>
                          <span v-if="caze.outcome_summary" class="case-outcome">
                            {{ truncateText(caze.outcome_summary, 160) }}
                          </span>
                        </div>
                      </template>
                    </div>
                    <div v-else class="corpus-lane-empty">
                      <i class="fas fa-notes-medical"></i>
                      No clinical practice patterns or similar cases matched this query.
                    </div>
                  </div>
                </div>

                <!-- Adverse Events (optional panel — collapsed by default) -->
                <div
                  v-if="msg.adverseEvents && msg.adverseEvents.total > 0"
                  class="message-adverse-events"
                >
                  <button class="ae-toggle" @click="toggleAdverseEvents(idx)">
                    <i class="fas fa-exclamation-triangle"></i>
                    {{ adverseEventsHeader(msg.adverseEvents) }}
                    <i :class="expandedAdverseEvents.has(idx) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                  </button>
                  <div v-if="expandedAdverseEvents.has(idx)" class="ae-panel">
                    <p class="ae-disclaimer">{{ msg.adverseEvents.disclaimer }}</p>
                    <div
                      v-for="(ev, ei) in msg.adverseEvents.events"
                      :key="ev.report_id || ei"
                      class="ae-item"
                      :class="{ 'ae-serious': ev.serious }"
                    >
                      <div class="ae-item-top">
                        <span v-if="ev.serious" class="ae-serious-badge">Serious</span>
                        <span class="ae-reactions">{{ (ev.reactions || []).slice(0, 3).join(', ') || 'Reaction reported' }}</span>
                      </div>
                      <div class="ae-context">
                        <span v-if="ev.species">{{ ev.species }}</span>
                        <span v-if="ev.breed"> · {{ ev.breed }}</span>
                        <span v-if="ev.weight"> · {{ ev.weight }}</span>
                        <span v-if="ev.receive_date"> · {{ ev.receive_date }}</span>
                      </div>
                      <div v-if="ev.outcomes && ev.outcomes.length" class="ae-outcomes">
                        Outcome: {{ ev.outcomes.join(', ') }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Agent summary collapsed line (appears once response is shown) -->
                <div v-if="msg.agentSummary" class="agent-summary-line">
                  <i class="fas fa-robot"></i> {{ msg.agentSummary }}
                </div>

                <!-- Follow-up chips (last assistant turn only) -->
                <div
                  v-if="msg.followUps && msg.followUps.length > 0 && idx === messages.length - 1"
                  class="follow-ups"
                >
                  <p class="follow-ups-label">You might also ask:</p>
                  <div class="follow-up-chips">
                    <button
                      v-for="suggestion in msg.followUps"
                      :key="suggestion"
                      class="follow-up-chip"
                      @click="usePrompt(suggestion)"
                    >{{ suggestion }}</button>
                  </div>
                </div>

                <!-- Save this turn (last assistant turn only) -->
                <div v-if="idx === messages.length - 1" class="turn-actions-row">
                  <button
                    v-if="!savedSearchId && !showSaveForm"
                    class="save-search-btn"
                    :disabled="savedSearchSaving"
                    title="Save this consult to My Library"
                    @click.stop="showSaveForm = true"
                  >
                    <i class="far fa-bookmark"></i>
                    <span>Save to Library</span>
                  </button>
                  <span v-else-if="savedSearchId" class="save-search-btn saved">
                    <i class="fas fa-bookmark"></i>
                    <span>Saved</span>
                  </span>
                </div>

              </div>
            </template>
          </div>
        </template>

        <!-- Inline Save Form -->
        <div v-if="showSaveForm && !savedSearchId" class="save-search-form">
          <div class="save-form-header">
            <i class="fas fa-bookmark"></i>
            <span>Save to My Library</span>
            <button class="save-form-close" @click="showSaveForm = false; saveNotes = ''">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <textarea
            v-model="saveNotes"
            class="save-notes-input"
            placeholder="Add a note for your reference (optional)"
            rows="2"
          />
          <div class="save-form-actions">
            <button
              class="save-confirm-btn"
              :disabled="savedSearchSaving"
              @click="saveSearch"
            >
              <i v-if="savedSearchSaving" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-bookmark"></i>
              {{ savedSearchSaving ? 'Saving…' : 'Save Search' }}
            </button>
            <button class="save-cancel-btn" @click="showSaveForm = false; saveNotes = ''">
              Cancel
            </button>
          </div>
        </div>

        <!-- Refine Search Card — shown after a result when server suggests narrowing -->
        <div v-if="refinePrompt && !loading" class="refine-search-card">
          <div class="refine-card-header">
            <span class="refine-card-icon"><i class="fas fa-filter"></i></span>
            <span class="refine-card-message">{{ refinePrompt.message }}</span>
            <button class="refine-card-dismiss" @click="dismissRefinePrompt" title="Dismiss">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="refine-suggestions">
            <button
              v-for="sug in refinePrompt.suggestions"
              :key="sug"
              class="refine-chip"
              @click="applyRefineContext(sug)"
            >
              <i class="fas fa-tag"></i> {{ sug }}
            </button>
          </div>
          <div class="refine-custom-row">
            <input
              v-model="relevanceContext"
              type="text"
              class="refine-custom-input"
              placeholder="Or type your own focus… e.g. 'equine studies post-operative'"
              @keydown.enter="applyRefineContext(relevanceContext)"
            />
            <button
              v-if="relevanceContext.trim()"
              class="refine-submit-btn"
              @click="applyRefineContext(relevanceContext)"
            >
              <i class="fas fa-search"></i> Refine
            </button>
          </div>
        </div>

        <!-- Engagement opt-in (honest prompt on weak results) -->
        <div v-if="engagementPrompt && !loading" class="engagement-card">
          <div class="engagement-header">
            <span class="engagement-icon"><i class="fas fa-circle-question"></i></span>
            <span class="engagement-message">{{ engagementPrompt.message }}</span>
            <button class="engagement-dismiss" @click="dismissEngagement" title="Dismiss">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="engagement-options">
            <button
              v-for="(o, i) in engagementPrompt.options"
              :key="i"
              class="engagement-btn"
              @click="applyEngagement(o)"
            >{{ o.label }}</button>
          </div>
        </div>

        <!-- Live source discovery results -->
        <div v-if="discovering || sourceCandidates.length || discoveryStatus" class="discovery-card">
          <div class="discovery-header">
            <i class="fas fa-satellite-dish"></i>
            <span v-if="discovering">{{ discoveryStatus || 'Searching live sources…' }}</span>
            <span v-else-if="sourceCandidates.length">Found {{ sourceCandidates.length }} source(s)</span>
            <span v-else>{{ discoveryStatus }}</span>
          </div>
          <div
            v-for="(c, i) in sourceCandidates"
            :key="i"
            class="discovery-item"
            :class="{ decided: c.decision }"
          >
            <div class="discovery-item-main">
              <a :href="c.url" target="_blank" rel="noopener" class="discovery-item-title">{{ c.title }}</a>
              <div class="discovery-item-meta">
                <span v-if="c.source_type" class="discovery-tag">{{ c.source_type }}</span>
                <span v-if="c.confidence != null" class="discovery-conf">
                  {{ Math.round(c.confidence * 100) }}% match
                </span>
                <span v-if="c.auto_added" class="discovery-auto">auto-added</span>
              </div>
            </div>
            <div class="discovery-item-actions">
              <template v-if="c.decision === 'added'">
                <span class="discovery-decided added"><i class="fas fa-check"></i> Added</span>
              </template>
              <template v-else-if="c.decision === 'rejected'">
                <span class="discovery-decided rejected"><i class="fas fa-times"></i> Dismissed</span>
              </template>
              <template v-else>
                <button class="discovery-add" @click="confirmCandidate(c)">
                  <i class="fas fa-plus"></i> Add
                </button>
                <button class="discovery-reject" @click="rejectCandidate(c)">
                  Not relevant
                </button>
              </template>
            </div>
          </div>
        </div>

        <!-- Agent Phases Progress (streaming) -->
        <div v-if="loading" class="message message-assistant">
          <div class="message-avatar ai-avatar">
            <i class="fas fa-robot"></i>
          </div>
          <div class="message-body">
            <!-- Live step list while streaming -->
            <div v-if="agentPhases.length > 0" class="agent-phases-list">
              <div class="agent-phases-header">
                <i class="fas fa-sitemap"></i> Deploying agents
              </div>
              <div
                v-for="(phase, idx) in agentPhases"
                :key="idx"
                class="agent-phase-item"
                :class="{ 'phase-done': phase.done, 'phase-error': phase.error }"
              >
                <span class="phase-icon">
                  <i v-if="phase.error" class="fas fa-exclamation-circle"></i>
                  <i v-else-if="phase.done" class="fas fa-check-circle"></i>
                  <i v-else class="fas fa-circle-notch fa-spin"></i>
                </span>
                <span class="phase-text">{{ phase.message }}</span>
                <span v-if="phase.meta" class="phase-meta">{{ phase.meta }}</span>
              </div>
            </div>

            <!-- Fallback dots if stream hasn't started yet -->
            <div v-else class="thinking-indicator">
              <span></span><span></span><span></span>
              <span class="thinking-label">Deploying agents…</span>
            </div>
          </div>
        </div>

        <!-- Soft upgrade prompt banner -->
        <div v-if="showUpgradePrompt && !loading" class="upgrade-banner">
          <i class="fas fa-bolt"></i>
          <span>You're using <strong>{{ usagePercent }}%</strong> of your monthly token quota.</span>
          <a href="/settings/billing" class="upgrade-banner-link">Upgrade plan</a>
          <button class="upgrade-banner-close" @click="showUpgradePrompt = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-area">
        <!-- Pending file attachment bar -->
        <div v-if="pendingFile" class="pending-file-bar">
          <i class="fas fa-paperclip"></i>
          <span class="pending-file-name">{{ pendingFile.name }}</span>
          <span class="pending-file-size">{{ formatFileSize(pendingFile.size) }}</span>
          <span v-if="uploadingFile" class="pending-file-status">
            <i class="fas fa-spinner fa-spin"></i> Uploading…
          </span>
          <button class="pending-file-remove" @click="clearPendingFile">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="input-wrapper">
          <!-- Hidden file input -->
          <input
            ref="fileInput"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.xls,.xlsx,.doc,.docx"
            style="display: none"
            @change="handleFileSelect"
          />

          <!-- Attach button -->
          <button
            class="attach-btn"
            :disabled="loading || !currentSessionId"
            @click="triggerFileUpload"
            :title="currentSessionId ? 'Attach a lab report or clinical document' : 'Start a consult first, then attach documents'"
          >
            <i class="fas fa-paperclip"></i>
          </button>

          <textarea
            ref="queryInput"
            v-model="query"
            class="query-input"
            :placeholder="messages.length > 0 ? 'Ask a follow-up question…' : 'Ask a clinical question or search for courses…'"
            :disabled="loading"
            rows="1"
            @keydown.enter.exact.prevent="submitQuery"
            @keydown.enter.shift.exact="null"
            @input="autoResize"
          />

          <div class="input-actions">
            <span class="depth-hint">
              <i :class="defaultDepth === 'quick' ? 'fas fa-bolt' : 'fas fa-microscope'"></i>
              {{ defaultDepth }}
            </span>
            <button
              class="submit-btn"
              :disabled="loading || !query.trim()"
              @click="submitQuery"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>

        <div
          v-if="needsTurnstile"
          ref="turnstileContainer"
          class="consult-turnstile"
        />

        <p class="input-hint">
          <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for new line ·
          <i class="fas fa-paperclip" style="font-size:0.75em"></i> attach labs/records ·
          Answers grounded in PubMed veterinary literature
        </p>
      </div>

      <!-- Attribution footer -->
      <div class="attribution">
        <i class="fas fa-info-circle"></i>
        AI responses are for informational purposes only and should not replace professional veterinary judgment.
        Always verify drug doses and protocols with current references.
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import authService from '@/services/auth'
import { useTurnstile } from '@/composables/useTurnstile'
import { renderConsultMarkdown } from '@/utils/consultMarkdown'
import CourseCard from '@/components/lms/CourseCard.vue'

export default {
  name: 'LmsAgentChat',

  components: {
    CourseCard,
  },

  props: {
    initialQuery: {
      type: String,
      default: '',
    },
    initialSessionId: {
      type: String,
      default: '',
    },
    /** 'quick' | 'deep' — set from Learning Hub hero before first ask */
    initialDepth: {
      type: String,
      default: 'quick',
    },
    /** Optional patient (ComplianceSubject) for chart-linked consults */
    complianceSubjectId: {
      type: String,
      default: '',
    },
  },

  emits: ['view-course', 'switch-tab'],

  setup(props, { emit }) {
    const router = useRouter()

    // ── Session state ──────────────────────────────────────────────────────
    const sessions = ref([])
    const loadingSessions = ref(false)
    const currentSessionId = ref(null)
    const currentSession = ref(null)
    const sidebarOpen = ref(true)
    const defaultDepth = ref(props.initialDepth === 'deep' ? 'deep' : 'quick')
    const patientContext = ref(null)

    const patientChartRoute = computed(() => {
      if (!props.complianceSubjectId) {
        return { path: '/learning', query: { tab: 'agent' } }
      }
      return {
        name: 'LearningPatientChart',
        params: {
          subjectId: String(props.complianceSubjectId),
        },
      }
    })

    const resolvedComplianceSubjectId = () => {
      const raw = props.complianceSubjectId
      if (!raw) return null
      const parsed = Number.parseInt(raw, 10)
      return Number.isNaN(parsed) ? null : parsed
    }

    // ── Turnstile (required for guests when site key is configured) ────────
    const turnstileContainer = ref(null)
    const turnstile = useTurnstile({ containerRef: turnstileContainer })
    const needsTurnstile = computed(() => {
      if (!turnstile.siteKey) return false
      const user = authService.getUser() || {}
      if (user.is_verified_clinician || authService.isVerifiedMember()) return false
      return true
    })
    // ── Conversation state ─────────────────────────────────────────────────
    const messages = ref([])
    const loading = ref(false)
    const expandedSources = ref(new Set())
    /** @type {import('vue').Ref<Record<number, 'evidence'|'clips'|'courses'|'cases'>>} */
    const corpusLaneByMsg = ref({})
    const selectedSliceByMsg = ref({})

    const expandedAllSources = ref(new Set())
    const expandedAdverseEvents = ref(new Set())

    // ── Input state ────────────────────────────────────────────────────────
    const query = ref('')
    const queryInput = ref(null)
    const fileInput = ref(null)
    const messagesContainer = ref(null)

    // ── Document upload ────────────────────────────────────────────────────
    const pendingFile = ref(null)
    const uploadingFile = ref(false)

    // ── Save state ─────────────────────────────────────────────────────────
    const savedSearchId = ref(null)
    const savedSearchSaving = ref(false)
    const showSaveForm = ref(false)
    const saveNotes = ref('')
    const savedPmids = ref(new Set())
    const savingPmids = ref(new Set())
    const feedbackFor = ref(null)
    const feedbackKind = ref(null)
    const correctionText = ref('')

    // ── Streaming agent state ──────────────────────────────────────────────
    // Live phase list shown while a stream is active
    const agentPhases = ref([])   // [{phase, message, meta, done}]
    // Refine-search prompt emitted by the server after scoring
    const refinePrompt = ref(null) // {message, suggestions[]}
    // Honest engagement on weak results + live source discovery state
    const engagementPrompt = ref(null) // {gate, message, options[]}
    const sourceCandidates = ref([])   // [{title, url, source_type, confidence, auto_added, source_id, decision}]
    const discovering = ref(false)
    const discoveryStatus = ref('')
    // Free-text relevance context the user can send with the next query
    const relevanceContext = ref('')
    // Feature gate flags received from the server on first stream event
    const featureGates = ref({
      deep_consult: true,
      llm_relevance_scoring: false,
      relevance_context: false,
    })

    // ── Usage quota (Phase 2) ──────────────────────────────────────────────
    const usageQuota = ref(null)  // {used_tokens, quota_limit, percentage_used, remaining}
    const quotaLoading = ref(false)
    const showUpgradePrompt = ref(false)

    // ── Grouped sessions (ChatGPT-style time bucketing) ───────────────────
    const groupedSessions = computed(() => {
      const now = new Date()
      const buckets = [
        { label: 'Today',      sessions: [] },
        { label: 'Yesterday',  sessions: [] },
        { label: 'This Week',  sessions: [] },
        { label: 'This Month', sessions: [] },
        { label: 'Older',      sessions: [] },
      ]
      sessions.value.forEach(session => {
        const d = new Date(session.updated_at)
        const diffDays = Math.floor((now - d) / 86400000)
        if      (diffDays === 0) buckets[0].sessions.push(session)
        else if (diffDays === 1) buckets[1].sessions.push(session)
        else if (diffDays < 7)  buckets[2].sessions.push(session)
        else if (diffDays < 30) buckets[3].sessions.push(session)
        else                    buckets[4].sessions.push(session)
      })
      return buckets.filter(b => b.sessions.length > 0)
    })

    // ── Constants ──────────────────────────────────────────────────────────
    const evidenceQualityLabels = {
      strong: 'Strong Evidence',
      moderate: 'Moderate Evidence',
      limited: 'Limited Evidence',
      no_evidence: 'Consensus Knowledge',
    }

    const suggestedPrompts = [
      'What CE courses do you have on feline diabetes management?',
      'Meloxicam dosing for dogs with osteoarthritis',
      'WSAVA vaccination guidelines — any related courses?',
      'Chronic kidney disease in cats — treatment protocols',
      'Canine parvovirus — latest evidence and prevention',
      'Anesthesia protocols for brachycephalic breeds',
    ]

    // ── Computed ───────────────────────────────────────────────────────────
    const currentEvidenceQuality = computed(() => {
      const lastAi = [...messages.value].reverse().find(m => m.role === 'assistant')
      return lastAi?.evidenceQuality || null
    })

    // ── Session management ─────────────────────────────────────────────────
    const loadSessions = async () => {
      loadingSessions.value = true
      try {
        const res = await api.get('/vet-consult/sessions/')
        sessions.value = res.data.results || []
      } catch (err) {
        console.error('Failed to load sessions:', err)
      } finally {
        loadingSessions.value = false
      }
    }

    const loadSessionById = async (sessionId) => {
      try {
        const res = await api.get(`/vet-consult/sessions/${sessionId}/`)
        const session = res.data
        currentSessionId.value = sessionId
        currentSession.value = session
        savedSearchId.value = null
        showSaveForm.value = false
        savedPmids.value = new Set()

        // Rebuild messages from stored turns
        messages.value = (session.turns || []).map((turn) => ({
          role: turn.role,
          content: turn.content,
          sources: turn.sources || [],
          adverseEvents: turn.adverse_events || null,
          courses: turn.courses || [],
          videoMoments: turn.video_moments || [],
          similarCases: turn.similar_cases || [],
          followUps: [],
          evidenceQuality: '',
          consultType: turn.consult_type || '',
          detectedSpecies: [],
          filename: turn.filename,
          file_size: turn.file_size,
          feedback: turn.feedback || null,
        }))

        // Citations open by default — the sourced answer is the product's
        // best feature, so never hide it behind a collapsed toggle.
        expandedSources.value = new Set(
          messages.value
            .map((m, idx) => (m.sources && m.sources.length ? idx : null))
            .filter((idx) => idx !== null)
        )
        corpusLaneByMsg.value = {}
        selectedSliceByMsg.value = {}
        messages.value.forEach((m, idx) => {
          if (m.role === 'assistant') {
            corpusLaneByMsg.value[idx] = defaultCorpusLane(m)
          }
        })

        nextTick(() => scrollToLatestUserTurn())
      } catch (err) {
        console.error('Failed to load session:', err)
      }
    }

    const startNewSession = () => {
      currentSessionId.value = null
      currentSession.value = null
      messages.value = []
      expandedSources.value = new Set()
      savedSearchId.value = null
      showSaveForm.value = false
      savedPmids.value = new Set()
      pendingFile.value = null
      nextTick(() => queryInput.value?.focus())
    }

    const deleteSession = async (sessionId) => {
      if (!confirm('Delete this consult? This cannot be undone.')) return
      try {
        await api.delete(`/vet-consult/sessions/${sessionId}/`)
        sessions.value = sessions.value.filter(s => s.id !== sessionId)
        if (currentSessionId.value === sessionId) {
          startNewSession()
        }
      } catch (err) {
        console.error('Failed to delete session:', err)
      }
    }

    // ── Submit query ───────────────────────────────────────────────────────

    // Phase icon map
    const phaseIcons = {
      deploying:   'fas fa-robot',
      memory:      'fas fa-brain',
      searching:   'fas fa-search',
      filtering:   'fas fa-filter',
      scoring:     'fas fa-chart-bar',
      courses:     'fas fa-graduation-cap',
      synthesizing:'fas fa-flask',
      rationale:   'fas fa-lightbulb',
    }

    const handleStreamEvent = (event) => {
      if (event.type === 'feature_gates') {
        featureGates.value = { ...featureGates.value, ...event.gates }
        return
      }

      if (event.type === 'status') {
        // Mark previous phase as done
        if (agentPhases.value.length > 0) {
          agentPhases.value[agentPhases.value.length - 1].done = true
        }
        const meta = event.source_count != null
          ? `${event.source_count} sources`
          : event.sources_used != null
            ? `${event.sources_used} used`
            : event.databases
              ? event.databases.join(' · ')
              : null
        agentPhases.value.push({
          phase: event.phase,
          icon: phaseIcons[event.phase] || 'fas fa-circle-notch',
          message: event.message,
          meta,
          done: false,
        })
        nextTick(() => scrollToBottom())
        return
      }

      if (event.type === 'refine_prompt') {
        refinePrompt.value = event
        return
      }

      if (event.type === 'engagement_prompt') {
        engagementPrompt.value = {
          gate: event.gate,
          message: event.message,
          options: event.options || [],
        }
        return
      }

      if (event.type === 'result') {
        const data = event.data
        // Mark last phase done
        if (agentPhases.value.length > 0) {
          agentPhases.value[agentPhases.value.length - 1].done = true
        }

        currentSessionId.value = data.session_id

        const assistantMsg = {
          role: 'assistant',
          content: data.answer || '',
          courses: data.courses || [],
          videoMoments: data.video_moments || [],
          similarCases: data.similar_cases || [],
          sources: data.sources || [],
          adverseEvents: data.adverse_events || null,
          followUps: data.follow_up_questions || [],
          evidenceQuality: data.evidence_quality || '',
          consultType: data.consult_type || '',
          detectedSpecies: data.detected_species || [],
          agentSummary: agentPhases.value.length
            ? `${agentPhases.value.length} agents · ${data.scored_source_count ?? data.sources?.length ?? 0} sources used`
            : null,
        }
        messages.value.push(assistantMsg)
        const msgIdx = messages.value.length - 1
        corpusLaneByMsg.value = {
          ...corpusLaneByMsg.value,
          [msgIdx]: defaultCorpusLane(assistantMsg),
        }
        if (data.sources?.length) autoExpandSources(msgIdx)

        if (!currentSession.value) currentSession.value = { consult_depth: defaultDepth.value }
        if (data.detected_species?.length) {
          currentSession.value = { ...currentSession.value, detected_species: data.detected_species.join(', ') }
        }
        if (data.notify_on_new_evidence !== undefined) {
          currentSession.value = { ...currentSession.value, notify_on_new_evidence: data.notify_on_new_evidence }
        }

        loadSessions()
        loadUsageQuota()
        nextTick(() => scrollToLatestUserTurn())
        return
      }

      if (event.type === 'error') {
        if (agentPhases.value.length > 0) {
          agentPhases.value[agentPhases.value.length - 1].done = true
          agentPhases.value[agentPhases.value.length - 1].error = true
        }
        messages.value.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          courses: [], sources: [], followUps: [], evidenceQuality: '',
        })
        return
      }
    }

    const submitQueryStream = async (queryText, ctxOverride) => {
      const baseURL = (process.env.VUE_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '')
      const token = authService.getToken()
      const ctx = ctxOverride ?? (relevanceContext.value.trim() || null)
      const turnstileToken = needsTurnstile.value ? (turnstile.token.value || '') : ''

      agentPhases.value = []
      refinePrompt.value = null
      engagementPrompt.value = null

      let streamSucceeded = false
      try {
        const response = await fetch(`${baseURL}/vet-consult/ask-stream/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : {}),
          },
          body: JSON.stringify({
            query: queryText,
            session_id: currentSessionId.value || null,
            consult_depth: defaultDepth.value,
            relevance_context: ctx || null,
            compliance_subject_id: resolvedComplianceSubjectId(),
            ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
          }),
        })

        if (response.status === 403) {
          const body = await response.json().catch(() => ({}))
          if (body.error === 'turnstile_failed') {
            messages.value.push({
              role: 'assistant',
              content: body.message || 'Please complete the bot check below and try again.',
              courses: [], sources: [], followUps: [], evidenceQuality: '',
            })
            turnstile.reset()
            return
          }
          if (body.feature === 'deep_consult') {
            messages.value.push({
              role: 'assistant',
              content: 'Deep Consult requires a Standard subscription or above. You can still use Quick Consult, or [upgrade your plan](/settings/billing) to unlock deeper research.',
              courses: [], sources: [], followUps: [], evidenceQuality: '',
            })
          }
          return
        }

        if (response.status === 429) {
          showUpgradePrompt.value = true
          const body = await response.json().catch(() => ({}))
          messages.value.push({
            role: 'assistant',
            content: body.message
              || "You've used your token quota for this billing period. [Upgrade your plan](/settings/billing) to continue.",
            courses: [], sources: [], followUps: [], evidenceQuality: '',
          })
          return
        }

        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        streamSucceeded = true

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() // keep incomplete line in buffer
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6))
                handleStreamEvent(event)
                if (event.type === 'done') break
              } catch { /* ignore malformed event */ }
            }
          }
        }

      } catch (streamErr) {
        console.warn('SSE stream failed, falling back to standard API:', streamErr)
        streamSucceeded = false
      }

      if (!streamSucceeded) {
        // Fallback: use the standard synchronous endpoint
        try {
          const res = await api.post('/vet-consult/ask/', {
            query: queryText,
            session_id: currentSessionId.value || null,
            consult_depth: defaultDepth.value,
            compliance_subject_id: resolvedComplianceSubjectId(),
            ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
          })
          const data = res.data
          currentSessionId.value = data.session_id
          const fallbackMsg = {
            role: 'assistant',
            content: data.answer || '',
            courses: data.courses || [],
            videoMoments: data.video_moments || [],
            similarCases: data.similar_cases || [],
            sources: data.sources || [],
            adverseEvents: data.adverse_events || null,
            followUps: data.follow_up_questions || [],
            evidenceQuality: data.evidence_quality || '',
            consultType: data.consult_type || '',
            detectedSpecies: data.detected_species || [],
          }
          messages.value.push(fallbackMsg)
          const fbIdx = messages.value.length - 1
          corpusLaneByMsg.value = {
            ...corpusLaneByMsg.value,
            [fbIdx]: defaultCorpusLane(fallbackMsg),
          }
          if (data.sources?.length) autoExpandSources(fbIdx)
          if (!currentSession.value) currentSession.value = { consult_depth: defaultDepth.value }
          if (data.detected_species?.length) {
            currentSession.value = { ...currentSession.value, detected_species: data.detected_species.join(', ') }
          }
          loadSessions()
          nextTick(() => scrollToLatestUserTurn())
        } catch (fallbackErr) {
          const errData = fallbackErr?.response?.data || {}
          messages.value.push({
            role: 'assistant',
            content: errData.message
              || (errData.error === 'turnstile_failed'
                ? 'Please complete the bot check below and try again.'
                : 'Sorry, I encountered an error. Please try again.'),
            courses: [], sources: [], followUps: [], evidenceQuality: '',
          })
          console.error('Fallback consult error:', fallbackErr)
        }
      }

      if (needsTurnstile.value) {
        turnstile.reset()
      }
    }

    const submitQuery = async () => {
      const q = query.value.trim()
      if (!q || loading.value) return
      if (needsTurnstile.value && !turnstile.canSubmit()) {
        messages.value.push({
          role: 'assistant',
          content: 'Please complete the bot check below, then send your question.',
          courses: [], sources: [], followUps: [], evidenceQuality: '',
        })
        return
      }

      messages.value.push({
        role: 'user',
        content: q,
        sources: [],
        courses: [],
        followUps: [],
        evidenceQuality: '',
      })

      query.value = ''
      loading.value = true
      savedSearchId.value = null
      showSaveForm.value = false
      relevanceContext.value = ''

      nextTick(() => {
        scrollToLatestUserTurn()
        if (queryInput.value) queryInput.value.style.height = 'auto'
      })

      try {
        await submitQueryStream(q)
      } finally {
        loading.value = false
        agentPhases.value = agentPhases.value.map(p => ({ ...p, done: true }))
      }
    }

    // Apply a refine suggestion or custom context and re-run the last query
    const applyRefineContext = (suggestion) => {
      relevanceContext.value = suggestion
      refinePrompt.value = null
      const lastUserMsg = [...messages.value].reverse().find(m => m.role === 'user')
      if (!lastUserMsg) return
      // Remove last assistant turn so it gets replaced
      const lastAiIdx = messages.value.map(m => m.role).lastIndexOf('assistant')
      if (lastAiIdx !== -1) messages.value.splice(lastAiIdx, 1)

      loading.value = true
      agentPhases.value = []
      submitQueryStream(lastUserMsg.content, suggestion).finally(() => {
        loading.value = false
        agentPhases.value = agentPhases.value.map(p => ({ ...p, done: true }))
        nextTick(() => scrollToBottom())
      })
    }

    const dismissRefinePrompt = () => {
      refinePrompt.value = null
      relevanceContext.value = ''
    }

    // ── Engagement opt-in + live source discovery ──────────────────────────
    const dismissEngagement = () => {
      engagementPrompt.value = null
    }

    const applyEngagement = (option) => {
      if (!option) return
      const lastUserMsg = [...messages.value].reverse().find(m => m.role === 'user')
      if (!lastUserMsg) return
      engagementPrompt.value = null
      if (option.action === 'search_broader') {
        // Re-run broader: bump to deep depth for a wider sweep.
        if (featureGates.value.deep_consult) defaultDepth.value = 'deep'
        loading.value = true
        agentPhases.value = []
        submitQueryStream(lastUserMsg.content, 'Search more broadly across all veterinary sources.').finally(() => {
          loading.value = false
          nextTick(() => scrollToBottom())
        })
      } else if (option.action === 'check_live_sources') {
        triggerDiscovery(lastUserMsg.content)
      }
    }

    const handleDiscoveryEvent = (event) => {
      switch (event.type) {
        case 'status':
          discoveryStatus.value = event.message || 'Searching live sources…'
          break
        case 'source_candidate': {
          const c = event.candidate || {}
          sourceCandidates.value.push({
            title: c.title,
            url: c.url,
            source_type: c.source_type,
            confidence: c.confidence,
            auto_added: c.auto_added || event.auto_added || false,
            source_id: c.source_id || null,
            decision: (c.auto_added || event.auto_added) ? 'added' : null,
          })
          break
        }
        case 'engagement_blocked':
          discoveryStatus.value = event.reason || 'Discovery is currently unavailable.'
          break
        case 'result':
          discoveryStatus.value = ''
          break
        case 'done':
          discovering.value = false
          break
        default:
          break
      }
    }

    const triggerDiscovery = async (queryText) => {
      const baseURL = (process.env.VUE_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '')
      const token = authService.getToken()
      discovering.value = true
      sourceCandidates.value = []
      discoveryStatus.value = 'Searching live sources…'
      try {
        const response = await fetch(`${baseURL}/agent/discovery/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ query: queryText, domain: 'veterinary' }),
        })
        if (response.status === 429) {
          discoveryStatus.value = "You've reached your usage limit for live discovery."
          discovering.value = false
          return
        }
        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`)
        }
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6))
                handleDiscoveryEvent(event)
              } catch { /* ignore malformed event */ }
            }
          }
        }
      } catch (err) {
        console.warn('Discovery stream failed:', err)
        discoveryStatus.value = 'Live discovery failed. Please try again.'
      } finally {
        discovering.value = false
      }
    }

    const confirmCandidate = async (candidate) => {
      if (!candidate || candidate.decision === 'rejected') return
      try {
        await api.post('/agent/discovery/sources/confirm/', candidate.source_id
          ? { source_id: candidate.source_id }
          : { url: candidate.url, title: candidate.title, domain: 'veterinary' })
        candidate.decision = 'added'
      } catch (e) {
        console.warn('Confirm source failed', e)
      }
    }

    const rejectCandidate = async (candidate, promote = false) => {
      if (!candidate) return
      try {
        await api.post('/agent/discovery/sources/reject/', {
          source_id: candidate.source_id || null,
          url: candidate.url,
          title: candidate.title,
          domain: 'veterinary',
          promote_to_profile: promote,
        })
        candidate.decision = 'rejected'
      } catch (e) {
        console.warn('Reject source failed', e)
      }
    }

    const usePrompt = (text) => {
      query.value = text
      nextTick(() => {
        autoResize()
        submitQuery()
      })
    }

    // ── Sources toggle ─────────────────────────────────────────────────────
    /** Open the research-sources panel for a new answer (cites-open default). */
    const autoExpandSources = (idx) => {
      const next = new Set(expandedSources.value)
      next.add(idx)
      expandedSources.value = next
    }

    const hasCorpusLanes = (msg) => {
      if (!msg || msg.role !== 'assistant') return false
      return true
    }

    const defaultCorpusLane = (msg) => {
      if (msg?.sources?.length) return 'evidence'
      if (msg?.similarCases?.length) return 'cases'
      if (msg?.videoMoments?.length) return 'clips'
      if (msg?.courses?.length) return 'courses'
      return 'evidence'
    }

    const activeCorpusLane = (idx) => {
      return corpusLaneByMsg.value[idx] || defaultCorpusLane(messages.value[idx])
    }

    const setCorpusLane = (idx, lane) => {
      corpusLaneByMsg.value = { ...corpusLaneByMsg.value, [idx]: lane }
    }

    const toggleSources = (idx) => {
      const next = new Set(expandedSources.value)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      expandedSources.value = next
    }

    const toggleShowAllSources = (idx) => {
      const next = new Set(expandedAllSources.value)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      expandedAllSources.value = next
    }

    const toggleAdverseEvents = (idx) => {
      const next = new Set(expandedAdverseEvents.value)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      expandedAdverseEvents.value = next
    }

    const adverseEventsHeader = (ae) => {
      if (!ae) return 'Adverse events'
      const serious = ae.serious_count || 0
      const total = ae.total || 0
      const level = ae.match_level || 'drug'
      const ctx = ae.query_context || {}
      let scope = 'this drug'
      if (level === 'weight' && ctx.weight_kg) scope = `~${ctx.weight_kg} kg patients`
      else if (level === 'breed' && ctx.breed) scope = ctx.breed + 's'
      else if (level === 'species' && ctx.species) scope = ctx.species
      if (serious > 0) {
        return `${serious} serious AE report${serious !== 1 ? 's' : ''} in ${scope}`
      }
      return `${total} adverse event${total !== 1 ? 's' : ''} in ${scope}`
    }

    const isSaveableSource = (source) => {
      const id = source?.pmid || ''
      const db = source?.source_db || ''
      if (db.includes('FDA') || id.startsWith('fda_') || id.startsWith('merck_') || id.startsWith('eclinpath_') || id.startsWith('ag_') || id.startsWith('ss_')) {
        return false
      }
      return /^\d+$/.test(String(id))
    }

    const downloadResponse = (content, idx) => {
      const blob = new Blob([content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `vetconsult-response-${idx + 1}.txt`
      a.click()
      URL.revokeObjectURL(url)
    }

    const submitFeedback = async (idx, kind, comment = '') => {
      if (!currentSessionId.value) return
      try {
        await api.post('/vet-consult/feedback/', {
          session_id: currentSessionId.value,
          turn_index: idx,
          kind,
          comment,
          raw_payload: { surface: 'vet_consult' },
        })
        const msg = messages.value[idx]
        if (msg) msg.feedback = { kind }
        if (window.$toast) {
          window.$toast.success(kind === 'up' ? 'Thanks — marked as helpful.' : 'Thanks — we logged that.')
        }
      } catch (err) {
        console.warn('vet consult feedback failed', err)
        if (window.$toast) {
          window.$toast.error('Could not record feedback. Please try again.')
        }
      }
    }

    const recordFeedback = async (idx, kind) => {
      feedbackFor.value = idx
      feedbackKind.value = kind
      if (kind === 'up') {
        await submitFeedback(idx, 'up')
        feedbackFor.value = null
        feedbackKind.value = null
      }
    }

    const cancelFeedback = () => {
      feedbackFor.value = null
      feedbackKind.value = null
      correctionText.value = ''
    }

    const submitCorrection = async () => {
      const idx = feedbackFor.value
      if (idx == null) {
        cancelFeedback()
        return
      }
      await submitFeedback(idx, 'down', (correctionText.value || '').trim())
      cancelFeedback()
    }

    // ── Evidence notifications ─────────────────────────────────────────────
    const toggleNotifications = async () => {
      if (!currentSessionId.value) return
      const newState = !(currentSession.value?.notify_on_new_evidence)
      try {
        await api.patch(
          `/vet-consult/sessions/${currentSessionId.value}/notifications/`,
          { enabled: newState },
        )
        currentSession.value = { ...currentSession.value, notify_on_new_evidence: newState }
        const sidebarSession = sessions.value.find(s => s.id === currentSessionId.value)
        if (sidebarSession) sidebarSession.notify_on_new_evidence = newState
      } catch (err) {
        console.error('Failed to toggle notifications:', err)
      }
    }

    // ── Save search ────────────────────────────────────────────────────────
    const saveSearch = async () => {
      if (savedSearchId.value || savedSearchSaving.value) return
      savedSearchSaving.value = true

      const lastUserMsg = [...messages.value].reverse().find(m => m.role === 'user')
      const lastAiMsg = [...messages.value].reverse().find(m => m.role === 'assistant')
      const q = lastUserMsg?.content || 'Consult'

      try {
        const name = q.length > 120 ? q.slice(0, 117) + '…' : q
        const response = await api.post('/lms/saved-searches/', {
          name,
          query: q,
          search_type: 'VETERINARY',
          filters: {},
          notes: saveNotes.value.trim(),
          result_snapshot: {
            answer: lastAiMsg?.content || '',
            evidence_quality: lastAiMsg?.evidenceQuality || '',
            consult_type: lastAiMsg?.consultType || '',
            detected_species: lastAiMsg?.detectedSpecies || [],
            sources: lastAiMsg?.sources || [],
            courses: lastAiMsg?.courses || [],
            snapshot_date: new Date().toISOString(),
          },
        })
        savedSearchId.value = response.data.id
        showSaveForm.value = false
      } catch {
        savedSearchSaving.value = false
        return
      }
      savedSearchSaving.value = false
    }

    // ── Save article ───────────────────────────────────────────────────────
    const saveArticle = async (source) => {
      const pmid = source.pmid
      if (savedPmids.value.has(pmid) || savingPmids.value.has(pmid)) return

      savingPmids.value = new Set([...savingPmids.value, pmid])
      const lastUserMsg = [...messages.value].reverse().find(m => m.role === 'user')

      try {
        const isRealPmid = pmid && !pmid.startsWith('ss_') && !pmid.startsWith('ag_')
        await api.post('/lms/saved-resources/save_from_search/', {
          pmid: isRealPmid ? pmid : null,
          external_id: isRealPmid ? null : pmid,
          resource_type: isRealPmid ? 'PUBMED' : 'EXTERNAL',
          title: source.title,
          url: source.url || `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`,
          authors: source.authors ? [source.authors] : [],
          abstract: source.abstract || '',
          journal: source.journal || '',
          pub_date: source.year || '',
          doi: source.doi || '',
          pub_type: [],
          source_db: source.source_db || 'PubMed',
          search_query: lastUserMsg?.content || '',
          saved_search_id: savedSearchId.value || null,
        })
        savedPmids.value = new Set([...savedPmids.value, pmid])
      } catch {
        // non-critical
      } finally {
        const next = new Set(savingPmids.value)
        next.delete(pmid)
        savingPmids.value = next
      }
    }

    // ── Document upload ────────────────────────────────────────────────────
    const triggerFileUpload = () => {
      fileInput.value?.click()
    }

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 50 * 1024 * 1024) {
        alert('File exceeds the 50 MB limit')
        return
      }
      pendingFile.value = file
      uploadPendingFile()
      event.target.value = ''
    }

    const clearPendingFile = () => {
      pendingFile.value = null
    }

    const uploadPendingFile = async () => {
      if (!pendingFile.value || !currentSessionId.value) return
      uploadingFile.value = true

      const formData = new FormData()
      formData.append('file', pendingFile.value)

      try {
        const res = await api.post(
          `/vet-consult/sessions/${currentSessionId.value}/upload/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        if (res.data.success) {
          messages.value.push({
            role: 'document',
            content: res.data.preview || '',
            filename: res.data.filename,
            file_size: pendingFile.value.size,
            sources: [],
            courses: [],
            followUps: [],
          })
          nextTick(() => scrollToBottom())
        } else {
          alert(`Upload failed: ${res.data.error || 'Unknown error'}`)
        }
      } catch (err) {
        console.error('Upload error:', err)
        alert('Upload failed. Please try again.')
      } finally {
        uploadingFile.value = false
        pendingFile.value = null
      }
    }

    // ── Utilities ──────────────────────────────────────────────────────────
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    }

    /** Scroll so the latest user question sits at the top of the thread. */
    const scrollToLatestUserTurn = () => {
      nextTick(() => {
        const container = messagesContainer.value
        if (!container) return
        const lastUserIdx = messages.value.map(m => m.role).lastIndexOf('user')
        if (lastUserIdx === -1) return
        const el = container.querySelector(`#message-${lastUserIdx}`)
        if (el) {
          el.scrollIntoView({ block: 'start', behavior: 'smooth' })
        }
      })
    }

    const truncateText = (text, maxLen = 160) => {
      if (!text) return ''
      const normalized = String(text).replace(/\s+/g, ' ').trim()
      if (normalized.length <= maxLen) return normalized
      return `${normalized.slice(0, maxLen - 1).trim()}…`
    }

    const diagnosisSliceFor = (pattern, label) => {
      if (!pattern?.diagnosis_slices?.length || !label) return null
      return pattern.diagnosis_slices.find((s) => s.label === label) || null
    }

    const selectedDiagnosisSlice = (msgIdx, caze) => {
      const label = selectedSliceByMsg.value[msgIdx]
      if (!label || !caze?.pattern) return null
      return diagnosisSliceFor(caze.pattern, label)
    }

    const selectDiagnosisSlice = (msgIdx, label) => {
      selectedSliceByMsg.value = { ...selectedSliceByMsg.value, [msgIdx]: label }
    }

    const clearDiagnosisSlice = (msgIdx) => {
      const next = { ...selectedSliceByMsg.value }
      delete next[msgIdx]
      selectedSliceByMsg.value = next
    }

    const diagnosisSliceSections = (slice) => {
      if (!slice?.facets) return []
      const specs = [
        { key: 'presenting_signals', title: 'Common Presenting Signs' },
        { key: 'treatments', title: 'Commonly Documented Treatments' },
        { key: 'medications', title: 'Medications' },
      ]
      return specs
        .map((s) => ({
          key: s.key,
          title: s.title,
          items: (slice.facets[s.key] || []).slice(0, 6),
        }))
        .filter((s) => s.items.length > 0)
    }

    /** Secondary pack facets on overview (diagnoses are primary + clickable). */
    const patternOverviewSections = (pattern) => {
      if (!pattern) return []
      const specs = [
        { key: 'differentials', title: 'Differentials' },
        { key: 'treatments', title: 'Treatments (plan)' },
        { key: 'medications', title: 'Medications' },
      ]
      return specs
        .map((s) => ({
          key: s.key,
          title: s.title,
          items: (pattern[s.key] || []).slice(0, 4),
        }))
        .filter((s) => s.items.length > 0)
    }

    const patternFacetSections = (pattern) => {
      if (!pattern) return []
      const specs = [
        { key: 'diagnoses', title: 'Common Diagnoses' },
        { key: 'differentials', title: 'Differentials' },
        { key: 'treatments', title: 'Treatments (plan)' },
        { key: 'medications', title: 'Medications' },
        { key: 'problems', title: 'Problem list' },
        { key: 'presenting_signals', title: 'Presenting signals' },
      ]
      return specs
        .map((s) => ({
          key: s.key,
          title: s.title,
          items: (pattern[s.key] || []).slice(0, 6),
        }))
        .filter((s) => s.items.length > 0)
    }

    const autoResize = () => {
      const el = queryInput.value
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 160) + 'px'
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const formatDate = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      const now = new Date()
      const diffDays = Math.floor((now - d) / 86400000)
      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays}d ago`
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }

    const consultTypeIcon = (type) => {
      const icons = {
        drug_dosing: 'fas fa-pills',
        differential_diagnosis: 'fas fa-search-plus',
        protocol: 'fas fa-list-ol',
        interpretation: 'fas fa-chart-bar',
        general: 'fas fa-comment-medical',
      }
      return icons[type] || 'fas fa-comment-medical'
    }

    /** Resolve consult course thumbnails the same way catalog pages do. */
    const resolveCourseThumbnail = (raw) => {
      if (!raw || typeof raw !== 'string') return null
      const thumbnail = raw.trim()
      if (!thumbnail || thumbnail === 'null' || thumbnail === 'undefined') return null
      if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://') || thumbnail.startsWith('data:')) {
        return thumbnail
      }
      let baseUrl = process.env.VUE_APP_API_URL || ''
      if (baseUrl.endsWith('/api')) baseUrl = baseUrl.slice(0, -4)
      if (thumbnail.startsWith('/media/')) return `${baseUrl}${thumbnail}`
      if (thumbnail.startsWith('media/')) return `${baseUrl}/${thumbnail}`
      if (thumbnail.startsWith('/')) return `${baseUrl}${thumbnail}`
      return `${baseUrl}/media/${thumbnail}`
    }

    const normalizeForCourseCard = (course) => ({
      ...course,
      thumbnail: resolveCourseThumbnail(course.thumbnail_url || course.thumbnail),
      category_name: course.category?.name || course.category_name || null,
    })

    const sourceIdLabel = (source) => {
      const db = source.source_db || 'PubMed'
      const id = source.pmid || ''
      if (db === 'FDA Animal Drug Label' || id.startsWith('fda_')) return 'FDA Label'
      if (db === 'Merck Veterinary Manual') return 'Merck Vet Manual'
      if (db === 'eClinPath (Cornell)') return 'eClinPath'
      if (db === 'AGRICOLA') return `AGRICOLA: ${id.replace('ag_', '')}`
      if (id.startsWith('ss_')) return `Semantic Scholar: ${id.replace('ss_', '')}`
      if (id && !id.startsWith('ss_') && !id.startsWith('ag_') && !id.startsWith('fda_') && !id.startsWith('merck_')) {
        return `PMID: ${id}`
      }
      return `ID: ${id}`
    }

    // Shared renderer — keeps citation links identical across the sample
    // widget, VetClinicalChat, and this view.
    const renderMarkdown = renderConsultMarkdown

    const handleViewCourse = (course) => emit('view-course', course)
    const handleEnroll = (course) => emit('view-course', course)

    /**
     * Open the video player at the matched moment in a new tab/window so the
     * consult answer stays on screen (demo-critical: no lost search context).
     *
     * Do NOT pass `noopener` in window.open's features string — browsers then
     * return null even on success, which used to falsely trigger same-tab
     * navigation and wipe the consult.
     */
    const jumpToMoment = (moment) => {
      const routeData = router.resolve({
        name: 'CourseVideoPlayer',
        params: {
          courseId: moment.course_id,
          videoId: moment.video_id,
        },
        query: {
          seek: moment.start_seconds,
          ...(moment.end_seconds != null ? { end: moment.end_seconds } : {}),
          ...(moment.is_enrolled ? {} : { preview: '1' }),
          from: 'consult',
          ...(currentSessionId.value ? { session: currentSessionId.value } : {}),
        },
      })
      const opened = window.open(routeData.href, '_blank')
      if (opened) {
        opened.opener = null
        return
      }
      // True popup block only — keep the consult; user can allow popups and retry.
      // Never router.push here: losing the chat is worse than a blocked clip.
      // eslint-disable-next-line no-alert
      window.alert('Allow pop-ups for this site to open video clips without leaving your consult.')
    }

    // ── Usage quota loader ─────────────────────────────────────────────────
    const usagePercent = computed(() => {
      if (!usageQuota.value) return 0
      const limit = Number(usageQuota.value.quota_limit)
      const used = Number(usageQuota.value.used_tokens)
      if (!Number.isFinite(limit) || limit <= 0 || !Number.isFinite(used)) return 0
      return Math.min(100, Math.round((used / limit) * 100))
    })
    const usageBarClass = computed(() => {
      const p = usagePercent.value
      if (p >= 90) return 'usage-fill-danger'
      if (p >= 70) return 'usage-fill-warn'
      return 'usage-fill-ok'
    })

    const loadUsageQuota = async () => {
      quotaLoading.value = true
      try {
        const res = await api.get('/users/billing/token_usage/')
        usageQuota.value = res.data
        if (res.data.percentage_used >= 80) showUpgradePrompt.value = true
      } catch {
        // Quota visibility is non-critical; ignore errors
      } finally {
        quotaLoading.value = false
      }
    }

    const loadPatientContext = async () => {
      const subjectId = resolvedComplianceSubjectId()
      if (!subjectId) {
        patientContext.value = null
        return
      }
      try {
        const res = await api.get(`/veterinary/patients/${subjectId}/chart/`)
        const data = res.data || {}
        patientContext.value = {
          name: data.entity?.name || 'Patient',
          projectId: data.project_id,
        }
      } catch (err) {
        console.warn('Could not load patient context for consult:', err)
        patientContext.value = null
      }
    }

    watch(
      () => props.complianceSubjectId,
      () => {
        loadPatientContext()
      },
    )

    // ── Lifecycle ──────────────────────────────────────────────────────────
    onMounted(async () => {
      loadSessions()
      loadUsageQuota()
      await loadPatientContext()
      if (needsTurnstile.value) {
        await nextTick()
        await turnstile.mount()
      }
      if (props.initialSessionId) {
        await loadSessionById(props.initialSessionId)
      } else if (props.initialQuery) {
        query.value = props.initialQuery
        await nextTick()
        autoResize()
        // Guests with Turnstile enabled must complete the widget before send —
        // leave the query filled rather than auto-firing a 403.
        if (!needsTurnstile.value) {
          submitQuery()
        }
      }
    })

    return {
      // session
      sessions,
      loadingSessions,
      currentSessionId,
      currentSession,
      sidebarOpen,
      defaultDepth,
      patientContext,
      patientChartRoute,
      // turnstile
      needsTurnstile,
      turnstileContainer,
      // conversation
      messages,
      loading,
      expandedSources,
      expandedAllSources,
      expandedAdverseEvents,
      currentEvidenceQuality,
      // input
      query,
      queryInput,
      fileInput,
      messagesContainer,
      // upload
      pendingFile,
      uploadingFile,
      // save
      savedSearchId,
      savedSearchSaving,
      showSaveForm,
      saveNotes,
      savedPmids,
      savingPmids,
      feedbackFor,
      feedbackKind,
      correctionText,
      // streaming / agent phases
      agentPhases,
      refinePrompt,
      engagementPrompt,
      sourceCandidates,
      discovering,
      discoveryStatus,
      relevanceContext,
      featureGates,
      // usage quota
      usageQuota,
      quotaLoading,
      showUpgradePrompt,
      usagePercent,
      usageBarClass,
      // constants
      evidenceQualityLabels,
      suggestedPrompts,
      groupedSessions,
      // methods
      loadSessionById,
      startNewSession,
      deleteSession,
      submitQuery,
      usePrompt,
      hasCorpusLanes,
      activeCorpusLane,
      setCorpusLane,
      toggleSources,
      toggleShowAllSources,
      toggleAdverseEvents,
      adverseEventsHeader,
      isSaveableSource,
      downloadResponse,
      recordFeedback,
      cancelFeedback,
      submitCorrection,
      toggleNotifications,
      saveSearch,
      saveArticle,
      triggerFileUpload,
      handleFileSelect,
      clearPendingFile,
      autoResize,
      formatFileSize,
      formatDate,
      consultTypeIcon,
      normalizeForCourseCard,
      sourceIdLabel,
      renderMarkdown,
      handleViewCourse,
      handleEnroll,
      jumpToMoment,
      truncateText,
      patternFacetSections,
      patternOverviewSections,
      diagnosisSliceSections,
      diagnosisSliceFor,
      selectedDiagnosisSlice,
      selectDiagnosisSlice,
      clearDiagnosisSlice,
      selectedSliceByMsg,
      applyRefineContext,
      dismissRefinePrompt,
      dismissEngagement,
      applyEngagement,
      confirmCandidate,
      rejectCandidate,
    }
  },
}
</script>

<style scoped>
/* ── Outer Layout ─────────────────────────────────── */
.lms-agent-chat {
  display: flex;
  height: calc(100vh - 280px);
  min-height: 520px;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

/* ── Sessions Sidebar (neutral shell — accent only for active/CTA) ── */
.sessions-sidebar {
  width: 0;
  min-width: 0;
  overflow: hidden;
  background: #fff;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
  transition: width 0.25s ease, min-width 0.25s ease;
  flex-shrink: 0;
}

.lms-agent-chat.sidebar-open .sessions-sidebar {
  width: 240px;
  min-width: 240px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 14px 10px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: #f8fafc;
}

.sidebar-header h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
  white-space: nowrap;
}

.sidebar-header h3 i {
  margin-right: 7px;
  color: #64748b;
}

.btn-new-session {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--p-accent, var(--color-primary, #0d9488));
  border: 1px solid var(--p-accent, var(--color-primary, #0d9488));
  color: #fff;
  padding: 5px 9px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: filter 0.15s;
}

.btn-new-session:hover {
  filter: brightness(1.06);
  color: #fff;
}

.depth-toggle-row {
  display: flex;
  gap: 5px;
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: #fff;
}

.depth-btn {
  flex: 1;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 5px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}

.depth-btn.active {
  background: var(--p-accent-subtle, #eef2f8);
  border-color: var(--p-accent, var(--color-primary, #0d9488));
  color: var(--p-accent, var(--color-primary, #0d9488));
  font-weight: 600;
}

.depth-btn:hover:not(.active) {
  background: #f1f5f9;
  color: #334155;
}

.session-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0 8px;
  background: #fff;
}

.session-list::-webkit-scrollbar {
  width: 4px;
}

.session-list::-webkit-scrollbar-track {
  background: transparent;
}

.session-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.sessions-loading {
  text-align: center;
  color: #94a3b8;
  padding: 16px;
  font-size: 13px;
}

/* ── Session Groups ────────────────────────────────── */
.session-group {
  margin-bottom: 2px;
}

.session-group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  padding: 10px 14px 4px;
}

.session-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 12px 8px 14px;
  cursor: pointer;
  transition: background 0.1s;
  border-left: 3px solid transparent;
  min-height: 48px;
}

.session-item:hover {
  background: #f8fafc;
}

.session-item.active {
  background: var(--p-accent-subtle, #eef2f8);
  border-left-color: var(--p-accent, var(--color-primary, #0d9488));
}

.session-icon {
  color: #94a3b8;
  font-size: 11px;
  flex-shrink: 0;
}

.session-item.active .session-icon {
  color: var(--p-accent, var(--color-primary, #0d9488));
}

.session-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.session-title {
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.session-item.active .session-title {
  color: var(--p-accent, var(--color-primary, #0d9488));
  font-weight: 600;
}

.session-meta {
  font-size: 10px;
  color: #94a3b8;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sessions-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
  padding: 28px 12px;
  text-align: center;
}

.sessions-empty i {
  font-size: 1.5rem;
  opacity: 0.5;
}

.session-notify-icon {
  color: var(--p-accent, var(--color-primary, #0d9488));
  font-size: 9px;
  flex-shrink: 0;
  opacity: 0.75;
}

.session-delete-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #cbd5e1;
  font-size: 11px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
  display: none;
}

.session-item:hover .session-delete-btn {
  display: inline-flex;
}

.session-delete-btn:hover {
  color: #dc2626;
  background: #fef2f2;
}

/* ── Chat Main ────────────────────────────────────── */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* ── Top Bar ──────────────────────────────────────── */
.chat-topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  min-height: 44px;
}

.sidebar-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #475569;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;
}

.sidebar-toggle-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: var(--p-accent, var(--color-primary, #0d9488));
  opacity: 1;
}

.lms-agent-chat.sidebar-open .sidebar-toggle-btn {
  border-color: var(--p-accent, var(--color-primary, #0d9488));
  color: var(--p-accent, var(--color-primary, #0d9488));
  background: var(--p-accent-subtle, #eef2f8);
}

.toggle-label {
  display: inline;
}

.topbar-center {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.species-tag {
  font-size: 11px;
  color: #059669;
  background: #d1fae5;
  padding: 3px 8px;
  border-radius: 20px;
  font-weight: 500;
}

.patient-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 11px;
  font-weight: 600;
  color: #1e3a5f;
  background: #eef2f8;
  padding: 3px 10px;
  border-radius: 20px;
  text-decoration: none;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.patient-context-chip:hover {
  background: #dbeafe;
}

.species-tag i {
  margin-right: 3px;
}

.evidence-badge {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 20px;
}

.quality-strong   { background: #d1fae5; color: #065f46; }
.quality-moderate { background: #fef3c7; color: #92400e; }
.quality-limited  { background: #fee2e2; color: #991b1b; }
.quality-no_evidence { background: #f1f5f9; color: #64748b; }

.notify-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1.5px solid #cbd5e1;
  background: #fff;
  color: #64748b;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.notify-btn:hover {
  border-color: #0ea5e9;
  color: #0284c7;
  background: #f0f9ff;
}

.notify-btn.active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: #fff;
}

.notify-label {
  display: inline;
}

/* ── Messages Container ───────────────────────────── */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Welcome State ────────────────────────────────── */
.welcome-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 20px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}

.welcome-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.35);
}

.welcome-icon i {
  font-size: 1.75rem;
  color: white;
}

.welcome-state h2 {
  font-size: 1.625rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.625rem;
}

.welcome-subtitle {
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 1.75rem;
  max-width: 520px;
}

.suggested-prompts h3 {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.prompt-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.625rem;
  width: 100%;
  max-width: 680px;
}

.prompt-chip {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  text-align: left;
  padding: 0.75rem 0.875rem;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  color: #374151;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1.5;
}

.prompt-chip:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f5f3ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.prompt-chip i {
  margin-top: 1px;
  flex-shrink: 0;
  color: #9ca3af;
}

.prompt-chip:hover i {
  color: #667eea;
}

/* ── Message Thread ───────────────────────────────── */
.message {
  display: flex;
  gap: 10px;
  max-width: 100%;
}

.message-user {
  flex-direction: row-reverse;
  align-self: flex-end;
  max-width: 75%;
}

.message-assistant {
  align-self: flex-start;
  max-width: 100%;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 2px;
}

.user-avatar {
  background: #1d4ed8;
  color: #fff;
}

.ai-avatar {
  background: #0f172a;
  color: #38bdf8;
}

.message-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.message-content {
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.65;
  color: #1e293b;
}

.user-content {
  background: #1d4ed8;
  color: #fff;
  border-bottom-right-radius: 4px;
  white-space: pre-wrap;
}

.ai-content {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-bottom-left-radius: 4px;
}

:deep(.ai-content p)        { margin: 0 0 7px; }
:deep(.ai-content p:last-child) { margin-bottom: 0; }
:deep(.ai-content h3)       { font-size: 14px; font-weight: 700; margin: 10px 0 5px; color: #0f172a; }
:deep(.ai-content h4)       { font-size: 13px; font-weight: 600; margin: 8px 0 4px; color: #1e293b; }
:deep(.ai-content ul)       { margin: 5px 0; padding-left: 18px; }
:deep(.ai-content li)       { margin-bottom: 3px; }
:deep(.ai-content code)     { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; font-size: 11px; font-family: monospace; }
:deep(.ai-content sup.citation)  { color: #2563eb; font-weight: 700; font-size: 10px; }
:deep(.ai-content a.citation-link) { text-decoration: none; }
:deep(.ai-content a.citation-link:hover sup.citation) { text-decoration: underline; }

/* ── Inline Video Moments ─────────────────────────── */
.inline-moments {
  background: #fff;
  border: 1.5px solid #0ea5e9;
  border-radius: 12px;
  padding: 14px;
  margin-top: 10px;
}

.inline-moments-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.inline-moments-header i {
  color: #0ea5e9;
  font-size: 1rem;
}

.inline-moments-header span:first-of-type {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2937;
}

.moments-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.moment-chip {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 9px 11px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.moment-chip:hover {
  background: #e0f2fe;
  border-color: #0ea5e9;
}

.moment-time {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  font-weight: 700;
  font-size: 0.8rem;
  color: #0369a1;
  font-variant-numeric: tabular-nums;
  padding-top: 1px;
}

.moment-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.moment-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
}

.moment-meta {
  font-size: 0.72rem;
  color: #64748b;
}

.moment-why {
  display: block;
  font-size: 0.8125rem;
  color: #334155;
  line-height: 1.45;
  margin-top: 4px;
}

.moment-match-reason {
  display: block;
  font-size: 0.75rem;
  color: #059669;
  margin-top: 4px;
}

.moment-match-reason i {
  margin-right: 4px;
}

.moment-citation-inline {
  display: block;
  margin: 4px 0 0;
  padding: 6px 8px;
  border-left: 3px solid #6366f1;
  background: #f5f3ff;
  font-size: 0.75rem;
  color: #334155;
  line-height: 1.4;
}

.moment-citation-inline .citation-time {
  display: block;
  font-weight: 600;
  color: #6366f1;
  margin-bottom: 2px;
}

.moment-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.keyword-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.6875rem;
  font-weight: 600;
}

/* ── Inline CE Courses ────────────────────────────── */
.corpus-lanes {
  margin-top: 0.85rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 10px;
  overflow: hidden;
  background: var(--surface-elevated, #fff);
}

.corpus-lane-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  background: var(--surface-muted, #f8fafc);
}

.corpus-lane-tab {
  flex: 1 1 auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.55rem 0.65rem;
  border: none;
  background: transparent;
  color: var(--text-secondary, #64748b);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.corpus-lane-tab:hover {
  color: var(--text-primary, #0f172a);
  background: rgba(15, 23, 42, 0.03);
}

.corpus-lane-tab.active {
  color: var(--brand-primary, #1d4ed8);
  border-bottom-color: var(--brand-primary, #1d4ed8);
  background: #fff;
}

.corpus-lane-tab .count-badge {
  font-size: 0.68rem;
}

.corpus-lane-panel {
  padding: 0.75rem;
}

.corpus-lane-empty {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  padding: 0.85rem 0.5rem;
  color: var(--text-secondary, #64748b);
  font-size: 0.85rem;
}

.corpus-lane-empty i {
  opacity: 0.7;
}

.cases-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.case-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
}

.case-card--pattern {
  border-color: #0d9488;
  background: rgba(13, 148, 136, 0.04);
}

.case-kind {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0d9488;
}

.case-title {
  font-weight: 600;
  font-size: 0.9rem;
}

.case-meta,
.case-outcome,
.case-dx {
  font-size: 0.8rem;
  color: var(--text-secondary, #64748b);
}

.case-pattern-facet {
  margin-top: 0.35rem;
}

.case-pattern-facet-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0f766e;
  margin-bottom: 0.1rem;
}

.case-pattern-dx {
  margin: 0.25rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.82rem;
  color: var(--text-primary, #334155);
}

.case-pattern-count {
  color: var(--text-secondary, #64748b);
  font-size: 0.78rem;
}

.case-pattern-dx--clickable {
  list-style: none;
  padding-left: 0;
}

.case-dx-link {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.3rem 0.35rem;
  border-radius: 6px;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.case-dx-link:hover:not(:disabled) {
  background: rgba(13, 148, 136, 0.1);
}

.case-dx-link:disabled {
  cursor: default;
  opacity: 0.85;
}

.case-dx-link span:first-child {
  flex: 1;
}

.case-dx-chevron {
  font-size: 0.7rem;
  color: #0d9488;
}

.case-slice-back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0.4rem 0 0.25rem;
  border: none;
  background: transparent;
  color: #0d9488;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.case-slice-header {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin: 0.25rem 0 0.35rem;
}

.case-slice-header h4 {
  margin: 0;
  font-size: 0.95rem;
}

.case-card--nested {
  margin-top: 0.4rem;
  background: #fff;
}

.case-pattern-facet--secondary {
  opacity: 0.92;
}

.inline-courses {
  background: #fff;
  border: 1.5px solid #667eea;
  border-radius: 12px;
  padding: 14px;
}

.inline-courses-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.inline-courses-header i {
  color: #667eea;
  font-size: 1rem;
}

.inline-courses-header span:first-of-type {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2937;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  background: #ede9fe;
  color: #7c3aed;
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: 20px;
}

.view-catalog-link {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: #667eea;
  background: none;
  border: 1px solid #c4b5fd;
  border-radius: 6px;
  padding: 3px 9px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.view-catalog-link:hover {
  background: #f5f3ff;
  border-color: #667eea;
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.875rem;
  align-items: stretch;
}

.courses-grid :deep(.course-card) {
  min-height: 0;
  height: auto;
}

.no-courses-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #6b7280;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border-radius: 8px;
}

.view-catalog-link-inline {
  background: none;
  border: none;
  color: #667eea;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

/* ── Research Sources ─────────────────────────────── */
.message-sources {
  margin-top: 2px;
}

.sources-toggle {
  background: none;
  border: none;
  font-size: 12px;
  color: #2563eb;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 0;
  transition: opacity 0.15s;
}

.sources-toggle:hover {
  opacity: 0.75;
}

.sources-list {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.source-item {
  padding: 8px 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
}

.source-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.source-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.source-db-badge {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.db-badge-pubmed           { background: #dbeafe; color: #1d4ed8; }
.db-badge-javma            { background: #fef3c7; color: #b45309; }
.db-badge-semantic-scholar { background: #ede9fe; color: #6d28d9; }
.db-badge-agricola         { background: #d1fae5; color: #065f46; }
.db-badge-merck-veterinary-manual { background: #fae8ff; color: #86198f; }
.db-badge-eclinpath-cornell       { background: #ffe4e6; color: #9f1239; }
.db-badge-fda-animal-drug-label   { background: #ffedd5; color: #c2410c; }

.source-journal {
  font-size: 0.75rem;
  font-weight: 600;
  color: #92400e;
}

.source-year {
  font-size: 0.6875rem;
  color: #9ca3af;
}

.save-article-btn {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1.5px solid #d1d5db;
  border-radius: 5px;
  color: #9ca3af;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
}

.save-article-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  background: #f5f3ff;
}

.save-article-btn.saved {
  border-color: #10b981;
  color: #10b981;
  background: #ecfdf5;
  cursor: default;
}

.save-article-btn:disabled:not(.saved) {
  opacity: 0.5;
  cursor: not-allowed;
}

.source-title-link {
  text-decoration: none;
  display: block;
}

.source-title-link:hover .source-title {
  color: #667eea;
  text-decoration: underline;
}

.source-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  margin: 0 0 3px;
  line-height: 1.4;
}

.source-rationale {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  margin: 0 0 3px;
  line-height: 1.5;
}

.source-rationale i {
  color: #f59e0b;
  margin-top: 1px;
  flex-shrink: 0;
}

.source-footer-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.source-pmid {
  font-size: 0.6875rem;
  color: #9ca3af;
  font-family: monospace;
}

.oa-pdf-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #dc2626;
  text-decoration: none;
  padding: 1px 5px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
}

/* ── Adverse Events panel ─────────────────────────── */
.message-adverse-events {
  margin-top: 8px;
}

.ae-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  color: #c2410c;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  width: 100%;
  text-align: left;
}

.ae-toggle:hover {
  background: #ffedd5;
}

.ae-toggle .fa-chevron-up,
.ae-toggle .fa-chevron-down {
  margin-left: auto;
  font-size: 0.65rem;
  opacity: 0.7;
}

.ae-panel {
  margin-top: 8px;
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
}

.ae-disclaimer {
  font-size: 0.6875rem;
  color: #92400e;
  margin: 0 0 10px;
  line-height: 1.4;
}

.ae-item {
  padding: 8px 0;
  border-top: 1px solid #fde68a;
}

.ae-item:first-of-type {
  border-top: none;
  padding-top: 0;
}

.ae-item-top {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.ae-serious-badge {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #fff;
  background: #dc2626;
  padding: 1px 6px;
  border-radius: 4px;
}

.ae-reactions {
  font-size: 0.75rem;
  font-weight: 600;
  color: #78350f;
}

.ae-context {
  font-size: 0.6875rem;
  color: #a16207;
  margin-top: 2px;
}

.ae-outcomes {
  font-size: 0.6875rem;
  color: #92400e;
  margin-top: 2px;
}

/* ── Follow-ups ───────────────────────────────────── */
.follow-ups {
  margin-top: 2px;
}

.follow-ups-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 6px;
}

.follow-up-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.follow-up-chip {
  padding: 4px 12px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 20px;
  color: #7c3aed;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
}

.follow-up-chip:hover {
  background: #ede9fe;
  border-color: #a78bfa;
}

/* ── Turn Actions (save) ──────────────────────────── */
.turn-actions-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.save-search-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  background: white;
  border: 1.5px solid #d1d5db;
  border-radius: 20px;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.save-search-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  background: #f5f3ff;
}

.save-search-btn.saved {
  border-color: #10b981;
  color: #065f46;
  background: #d1fae5;
  cursor: default;
}

.save-search-btn:disabled:not(.saved) {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Save Form ────────────────────────────────────── */
.save-search-form {
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 4px;
}

.save-form-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.save-form-header i {
  color: #f59e0b;
}

.save-form-close {
  margin-left: auto;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1;
}

.save-form-close:hover {
  color: #4b5563;
  background: #e5e7eb;
}

.save-notes-input {
  width: 100%;
  padding: 8px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #374151;
  resize: none;
  line-height: 1.5;
  background: white;
  box-sizing: border-box;
  transition: border-color 0.15s;
  font-family: inherit;
}

.save-notes-input:focus {
  outline: none;
  border-color: #667eea;
}

.save-form-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.save-confirm-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

.save-confirm-btn:hover:not(:disabled) { opacity: 0.9; }
.save-confirm-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.save-cancel-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background 0.15s;
}

.save-cancel-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

/* ── Document Turn ────────────────────────────────── */
.message-doc-turn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background: #f0f9ff;
  border: 1px dashed #7dd3fc;
  border-radius: 10px;
  font-size: 12px;
  color: #0369a1;
  align-self: center;
  max-width: 480px;
}

.doc-filename {
  font-weight: 600;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-size {
  font-size: 10px;
  color: #94a3b8;
}

.doc-status {
  font-size: 10px;
  color: #059669;
  font-weight: 500;
}

/* ── Thinking Indicator ───────────────────────────── */
.thinking-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
}

.thinking-indicator span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #94a3b8;
  animation: thinking-pulse 1.2s ease-in-out infinite;
}

.thinking-indicator span:nth-child(2) { animation-delay: 0.2s; }
.thinking-indicator span:nth-child(3) { animation-delay: 0.4s; }

.thinking-label {
  background: none !important;
  animation: none !important;
  width: auto !important;
  height: auto !important;
  border-radius: 0 !important;
  font-size: 12px;
  color: #94a3b8;
  margin-left: 4px;
}

@keyframes thinking-pulse {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40%           { transform: scale(1.2); opacity: 1; }
}

/* ── Input Area ───────────────────────────────────── */
.input-area {
  padding: 12px 16px 8px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.pending-file-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 12px;
}

.pending-file-bar i.fa-paperclip {
  color: #2563eb;
}

.pending-file-name {
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pending-file-size {
  color: #64748b;
  font-size: 10px;
}

.pending-file-status {
  color: #2563eb;
  font-size: 10px;
}

.pending-file-remove {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 3px;
  transition: color 0.1s;
}

.pending-file-remove:hover {
  color: #ef4444;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 10px;
  transition: border-color 0.15s;
}

.input-wrapper:focus-within {
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
  background: #fff;
}

.attach-btn {
  width: 30px;
  height: 30px;
  background: none;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.15s;
  flex-shrink: 0;
}

.attach-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  background: #f5f3ff;
}

.attach-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.query-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.9375rem;
  color: #1e293b;
  resize: none;
  max-height: 160px;
  overflow-y: auto;
  line-height: 1.5;
  font-family: inherit;
}

.query-input::placeholder {
  color: #94a3b8;
}

.query-input:disabled {
  opacity: 0.6;
}

.input-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.depth-hint {
  font-size: 10px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 3px;
  text-transform: capitalize;
}

.submit-btn {
  width: 34px;
  height: 34px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.submit-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.submit-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.consult-turnstile {
  margin-top: 10px;
  min-height: 65px;
  display: flex;
  justify-content: center;
}

.input-hint {
  font-size: 10px;
  color: #9ca3af;
  margin: 5px 0 0;
  text-align: center;
}

.input-hint kbd {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 9px;
  font-family: monospace;
}

/* ── Attribution ──────────────────────────────────── */
.attribution {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.5;
  padding: 6px 16px 10px;
  background: #fff;
  flex-shrink: 0;
}

.attribution i {
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Agent Phases List ────────────────────────────── */
.agent-phases-list {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 260px;
  max-width: 440px;
}

.agent-phases-header {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.agent-phase-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: #64748b;
  transition: color 0.2s;
}

.agent-phase-item.phase-done {
  color: #374151;
}

.agent-phase-item.phase-error {
  color: #dc2626;
}

.phase-icon {
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.phase-icon .fa-check-circle {
  color: #10b981;
}

.phase-icon .fa-exclamation-circle {
  color: #dc2626;
}

.phase-icon .fa-circle-notch {
  color: #667eea;
}

.phase-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.phase-meta {
  font-size: 0.7rem;
  color: #9ca3af;
  background: #f1f5f9;
  padding: 1px 6px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Agent summary line (collapsed after result) ──── */
.download-response-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 0.75rem;
  color: #64748b;
  cursor: pointer;
  margin-top: 6px;
  transition: all 0.15s;
}

.download-response-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #334155;
}

.message-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
}

.message-toolbar .download-response-btn {
  margin-top: 0;
}

.feedback-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 28px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}

.feedback-btn:hover:not(:disabled) {
  background: #f1f5f9;
  color: #334155;
}

.feedback-btn.active {
  background: #ecfdf5;
  border-color: #6ee7b7;
  color: #047857;
}

.feedback-btn:disabled {
  cursor: default;
  opacity: 0.85;
}

.correction-box {
  margin-top: 8px;
  padding: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.correction-box textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 0.8125rem;
  resize: vertical;
}

.correction-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.correction-cancel,
.correction-submit {
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.75rem;
  cursor: pointer;
}

.correction-cancel {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
}

.correction-submit {
  background: #0f766e;
  border: 1px solid #0f766e;
  color: #fff;
}

.sources-see-more-btn {
  background: none;
  border: none;
  font-size: 0.75rem;
  color: #2563eb;
  cursor: pointer;
  padding: 4px 2px;
  margin-top: 2px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.15s;
}

.sources-see-more-btn:hover {
  opacity: 0.75;
}

.agent-summary-line {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 2px 0 6px;
}

.agent-summary-line i {
  font-size: 0.7rem;
  opacity: 0.7;
}

/* ── Refine Search Card ────────────────────────────── */
.refine-search-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e8f5e9 100%);
  border: 1.5px solid #bae6fd;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 4px 0 8px 44px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 540px;
}

/* Engagement opt-in card */
.engagement-card {
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 4px 0 8px 44px;
  max-width: 540px;
}
.engagement-header {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1d4ed8;
  margin-bottom: 8px;
}
.engagement-icon { color: #2563eb; }
.engagement-message { flex: 1; }
.engagement-dismiss {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}
.engagement-options { display: flex; flex-wrap: wrap; gap: 8px; }
.engagement-btn {
  padding: 5px 12px;
  background: #fff;
  border: 1px solid #93c5fd;
  border-radius: 14px;
  cursor: pointer;
  font-size: 0.8rem;
  color: #1d4ed8;
}
.engagement-btn:hover { background: #dbeafe; }

/* Live source discovery card */
.discovery-card {
  background: #fafafa;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 4px 0 8px 44px;
  max-width: 580px;
}
.discovery-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}
.discovery-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid #eef2f7;
}
.discovery-item.decided { opacity: 0.7; }
.discovery-item-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  text-decoration: none;
}
.discovery-item-title:hover { text-decoration: underline; }
.discovery-item-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 3px;
  font-size: 0.72rem;
  color: #64748b;
}
.discovery-tag {
  background: #e2e8f0;
  border-radius: 8px;
  padding: 1px 7px;
}
.discovery-auto { color: #16a34a; font-weight: 600; }
.discovery-item-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  align-items: center;
}
.discovery-add,
.discovery-reject {
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  cursor: pointer;
  border: 1px solid #cbd5e1;
  background: #fff;
}
.discovery-add { color: #16a34a; border-color: #86efac; }
.discovery-add:hover { background: #f0fdf4; }
.discovery-reject { color: #64748b; }
.discovery-reject:hover { background: #f1f5f9; }
.discovery-decided { font-size: 0.78rem; font-weight: 600; }
.discovery-decided.added { color: #16a34a; }
.discovery-decided.rejected { color: #94a3b8; }

.refine-card-header {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0369a1;
}

.refine-card-icon {
  color: #0284c7;
  font-size: 0.8rem;
}

.refine-card-message {
  flex: 1;
}

.refine-card-dismiss {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s;
}

.refine-card-dismiss:hover {
  color: #6b7280;
}

.refine-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.refine-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: white;
  border: 1.5px solid #7dd3fc;
  border-radius: 20px;
  color: #0369a1;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
}

.refine-chip:hover {
  background: #e0f2fe;
  border-color: #38bdf8;
}

.refine-chip i {
  font-size: 0.65rem;
  opacity: 0.7;
}

.refine-custom-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.refine-custom-input {
  flex: 1;
  padding: 6px 10px;
  border: 1.5px solid #bae6fd;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #1e293b;
  background: white;
  outline: none;
  transition: border-color 0.15s;
}

.refine-custom-input:focus {
  border-color: #38bdf8;
}

.refine-submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  background: #0284c7;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}

.refine-submit-btn:hover {
  background: #0369a1;
}

/* ── Usage meter chip (topbar) ────────────────────── */
.usage-meter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 11px;
  color: #64748b;
}

.usage-meter-chip.usage-warn {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.usage-meter-chip.usage-danger {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #991b1b;
}

.usage-chip-bar {
  width: 44px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.usage-chip-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.usage-fill-ok { background: #10b981; }
.usage-fill-warn { background: #f59e0b; }
.usage-fill-danger { background: #ef4444; }

.usage-chip-label {
  font-weight: 600;
  font-size: 10px;
}

/* ── Upgrade banner ───────────────────────────────── */
.upgrade-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fffbeb;
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  padding: 9px 14px;
  margin: 4px 0 8px 44px;
  font-size: 0.8125rem;
  color: #78350f;
  max-width: 520px;
}

.upgrade-banner i.fa-bolt {
  color: #f59e0b;
  flex-shrink: 0;
}

.upgrade-banner-link {
  color: #b45309;
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
  margin-left: 2px;
}

.upgrade-banner-close {
  margin-left: auto;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
}

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 768px) {
  .lms-agent-chat {
    height: calc(100vh - 200px);
  }

  .lms-agent-chat.sidebar-open .sessions-sidebar {
    width: 200px;
    min-width: 200px;
  }

  .toggle-label,
  .notify-label {
    display: none;
  }

  .courses-grid {
    grid-template-columns: 1fr;
  }

  .prompt-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .lms-agent-chat.sidebar-open .sessions-sidebar {
    position: absolute;
    z-index: 10;
    height: 100%;
    width: 220px;
    min-width: 220px;
  }
}
</style>
